import { useRef, useEffect, useContext } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { SUPER_PROJECT_NAME, SVG_SIZE_S, SVG_SIZE_M } from 'src/constants';
import { Button } from 'src/v2/commonComponents/Button';
import { Icon } from 'src/components/Icon';
import styles from './SNLink.module.scss';
import { HoverTooltip } from 'src/v2/commonComponents/HoverTooltip';
import {
  useCurrentConversation,
  useOutsideClick,
  useRedirectToSNWithParams,
  useVisible,
} from 'src/hooks';
import { SuperNinjaTooltip } from 'src/v2/components/SuperNinjaTooltip';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import classNames from 'classnames';

const FIRST_RENDER_TOOLTIP_ID = 'input-box-sn-first-render-tooltip';
const HOVER_TOOLTIP_ID = 'input-box-sn-hover-tooltip';

export const SNLink = () => {
  const { attachmentLoading } = useContext(ThreadInputBoxContext);

  const { href } = useRedirectToSNWithParams();

  const containerRef = useRef<HTMLDivElement>(null);

  const { isVisible, onVisibilityRemove } = useVisible(true);

  const { isCurrentConversationLoading } = useCurrentConversation();

  useOutsideClick(containerRef, onVisibilityRemove);

  useEffect(() => {
    const timer = setTimeout(() => {
      onVisibilityRemove();
    }, 10000);

    return () => {
      clearTimeout(timer);
    };
  }, [onVisibilityRemove]);

  return (
    <>
      <HoverTooltip
        className={styles.firstRenderTooltip}
        id={FIRST_RENDER_TOOLTIP_ID}
        isOpen={!isCurrentConversationLoading && isVisible}
        shouldShowForMobile
      />

      <div
        className={styles.root}
        ref={containerRef}
        onMouseEnter={onVisibilityRemove}
        onMouseLeave={onVisibilityRemove}
        data-tooltip-id={FIRST_RENDER_TOOLTIP_ID}
        data-tooltip-place="top-start"
        data-tooltip-html={renderToStaticMarkup(<SuperNinjaTooltip />)}
      >
        <div>
          <HoverTooltip id={HOVER_TOOLTIP_ID} />

          <Button
            color="secondary-outline"
            as="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(styles.button, {
              [styles.disabled]: attachmentLoading,
            })}
            data-tooltip-id={HOVER_TOOLTIP_ID}
            data-tooltip-place="top-start"
            data-tooltip-content="Advanced agent with its own computer"
          >
            <Icon type="superNinjaMainIcon" size={SVG_SIZE_M} />

            {SUPER_PROJECT_NAME}

            <ArrowSquareOut size={SVG_SIZE_S} />
          </Button>
        </div>
      </div>
    </>
  );
};
